import { api, enc, qs } from '../api';
import { fmtDate, fmtWhen, money, plural } from '../format';
import { go, withQuery } from '../router';
import { useApp, useLoad } from '../state';
import { Empty, Facts, Ladder, LedgerList, Loading, MidDisplay, MoveButtons, Notes, PageHead, Pill, Ref, Sub, useMoveDialog } from '../ui/bits';
import { Choice, opts, words } from '../ui/fields';
import { useCampaignDialogs, usePartyOpts } from './dialogs';
import { HubSpotSection, Scorecard } from './HubSpot';
import { LinksAndPages } from './Web';
import { WorkforceStrip } from './Workforce';
import { TeamworkChip } from '../ui/teamwork_chip';
import { HumanAvatar } from '../ui/avatar';
import { CampaignFiles } from '../ui/campaign_files';
import { IntakeCard } from '../ui/intake_card';

export function CampaignList({ query }: { query: Record<string, string> }) {
  const { can, vocab } = useApp();
  const { newCampaign } = useCampaignDialogs();
  const partyOpts = usePartyOpts();
  const keys = ['year', 'lifecycle', 'owner', 'q', 'channel', 'include_archived'];
  const { data, error } = useLoad(() => api('GET', `/campaigns?${qs(Object.fromEntries(keys.map((k) => [k, query[k]])))}`), [JSON.stringify(query)]);
  const campaigns: any[] = data?.campaigns ?? [];
  const years = [...new Set([2025, 2026, 2027, new Date().getFullYear()])].sort().map(String);
  return (<>
    <PageHead title="Campaigns" sub={data ? `${plural(campaigns.length, 'campaign')} · one row per MID` : 'One row per MID'}
      actions={can('marketer') && <button onClick={newCampaign}>New campaign</button>} />
    <form className="toolbar" key={JSON.stringify(query)} onSubmit={(ev) => { ev.preventDefault(); go(withQuery('#/campaigns', ev.currentTarget)); }}>
      <input type="search" name="q" placeholder="Search MID or name" defaultValue={query.q || ''} />
      <Choice name="year" options={words(years)} value={query.year} blank="Any year" />
      <Choice name="lifecycle" options={words(['new', 'scheduled', 'active', 'closing', 'retired'])} value={query.lifecycle} blank="Any lifecycle" />
      <Choice name="channel" options={opts(vocab.channel.filter((c) => !c.parent_code))} value={query.channel} blank="Any channel" />
      <Choice name="owner" options={partyOpts} value={query.owner} blank="Any owner" />
      <label className="check"><input type="checkbox" name="include_archived" value="1" defaultChecked={!!query.include_archived} /> include archived</label>
      <button className="quiet" type="submit">Filter</button>
    </form>
    {error ? <Empty>{error}</Empty> : !data ? <Loading /> : campaigns.length ? (
      <div className="card"><div className="table-wrap"><table><thead><tr><th>MID</th><th>Campaign</th><th>Status</th><th>Owner</th><th>Offer</th><th>Planned</th><th className="num">Tracks</th><th className="num">Budget</th></tr></thead><tbody>
        {campaigns.map((c) => (
          <tr key={c.mid}><td><Ref to={`#/campaigns/${c.mid}`}>{c.mid}</Ref></td><td><a href={`#/campaigns/${c.mid}`} style={{ fontWeight: 600, color: 'inherit' }}>{c.name}</a><Sub>{c.purpose_name || ''}{c.importance ? ` · ${c.importance}` : ''}</Sub></td>
            <td><Pill code={c.status_code} /></td><td>{c.owner_name ? <span className="row" style={{ gap: 8 }}><HumanAvatar name={c.owner_name} size="s" /><span className="small">{c.owner_name}</span></span> : '—'}</td><td>{c.offer_code || '—'}</td>
            <td className="nowrap small">{fmtDate(c.planned_start)} → {fmtDate(c.planned_end)}</td><td className="num">{c.track_count}</td><td className="num mono">{money(c.budget_cents, c.currency)}</td></tr>
        ))}
      </tbody></table></div></div>
    ) : <Empty>No campaigns match. {can('marketer') && <button className="link" onClick={newCampaign}>Create the first one</button>}</Empty>}
  </>);
}

export function CampaignDetail({ mid }: { mid: string }) {
  const { can, me } = useApp();
  const { editCampaign, newTrack } = useCampaignDialogs();
  const moveDialog = useMoveDialog();
  const { data, error, reload } = useLoad(async () => {
    const [d, l] = await Promise.all([api('GET', `/campaigns/${enc(mid)}`), api('GET', `/campaigns/${enc(mid)}/ledger?limit=60`)]);
    return { d, ledger: l.ledger.filter((x: any) => x.entity_type === 'campaign') };
  }, [mid]);
  if (error) return <Empty>{error}</Empty>;
  if (!data) return <Loading />;
  const { d, ledger } = data;
  const c = d.campaign, open = d.open_step;
  const moveTrack = async (tid: string) => { const t = await api('GET', `/tracks/${enc(tid)}`); moveDialog('track', t.track.tid, t.track.status_code, t.moves, reload); };
  return (<>
    <PageHead pre={<MidDisplay id={c.mid} />} title={c.name}
      sub={<>{c.purpose_name || 'No purpose set'} · {c.importance} · {c.cadence_type}{c.supersedes_mid && <> · supersedes <Ref to={`#/campaigns/${c.supersedes_mid}`}>{c.supersedes_mid}</Ref></>}</>}
      actions={<>{can('marketer') && <button className="quiet" onClick={() => editCampaign(d)}>Edit header</button>}{can('marketer') && c.status_code < 999 && <button onClick={() => newTrack(c)}>New track</button>}</>} />
    <Ladder current={c.status_code} moves={d.moves} />
    <div className="muted small">Now {c.status_name} since {fmtWhen(open?.entered_at)}{open?.planned_exit_at ? `, due ${fmtDate(open.planned_exit_at)}` : ''}{c.actual_start ? ` · started ${fmtDate(c.actual_start)}` : ''}{c.actual_end ? ` · ended ${fmtDate(c.actual_end)}` : ''}</div>
    {d.intake && <IntakeCard intake={d.intake} mid={c.mid} canResubmit={can('manager') || (!!me.party?.id && me.party.id === c.owner_party_id) || (!!me.id && me.id === c.created_by)} reload={reload} />}
    {!d.intake?.open && <MoveButtons moves={d.moves} onMove={(to) => moveDialog('campaign', c.mid, c.status_code, d.moves, reload, to)} />}
    <WorkforceStrip mid={c.mid} canEdit={can('manager') || (!!me.party?.id && me.party.id === c.owner_party_id)} />
    <TeamworkChip mid={c.mid} canEdit={can('manager') || (!!me.party?.id && me.party.id === c.owner_party_id)} />
    <div className="cols" style={{ marginTop: 22 }}>
      <div><section>
        <Facts items={[
          ['Owner', <>{c.owner_name || '—'}{d.assignments.length > 0 && <Sub>{d.assignments.map((a: any) => `${a.party_name} (${a.role})`).join(', ')}</Sub>}</>],
          ['Offer', c.offer_name ? <>{c.offer_name} <span className="muted">{c.offer_code}</span></> : '—'],
          ['Planned', <>{fmtDate(c.planned_start)} → {fmtDate(c.planned_end)}{c.duration_days ? ` (${c.duration_days} d)` : ''}</>],
          ['Budget', <>{money(c.budget_cents, c.currency)}{d.spend && <Sub>spent {money(Math.round(d.spend.spent * 100))}, {d.spend.entries} entries</Sub>}</>],
          ['Channels', d.channels.map((x: any) => x.name).join(', ') || '—'],
          ['Audience', d.personas.map((x: any) => x.name).join(', ') || '—'],
          ['Delivery', <>{c.delivery_window || '—'}{c.cadence_detail ? ` · ${c.cadence_detail}` : ''}</>],
          ['Goal', c.goal || '—'],
          ['KPI notes', c.kpi_notes || '—'],
          c.legacy_mid && ['Legacy ID', <span className="mono">{c.legacy_mid}</span>],
        ]} />
        {c.description && <p style={{ marginTop: 10 }}>{c.description}</p>}
      </section></div>
      <aside className="side">
        <section><h3>Ledger</h3><LedgerList rows={ledger} /></section>
        <Notes notes={d.notes} entityType="campaign" entityId={c.id} onAdded={reload} />
      </aside>
    </div>
    <section>
      <h2>Assets <span className="muted">{d.files?.length ?? 0}</span></h2>
      <p className="muted small" style={{ marginTop: -6, marginBottom: 10 }}>What has already been made toward the goal. The agents working this campaign read these.</p>
      <CampaignFiles mid={c.mid} files={d.files ?? []} canEdit={can('contributor')} onChange={reload} />
    </section>
    <section>
      <h2>Tracks <span className="muted">{d.tracks.length}</span></h2>
      {d.tracks.length ? (
        <div className="table-wrap"><table><thead><tr><th>TID</th><th>Track</th><th>Status</th><th>Owner</th><th>Step due</th><th></th></tr></thead><tbody>
          {d.tracks.map((t: any) => (
            <tr key={t.tid}><td><Ref to={`#/tracks/${t.tid}`}>{t.tid}</Ref></td>
              <td>{t.name}<Sub>{t.track_type_name || ''}{t.channel_name ? ` · ${t.channel_name}` : ''}{t.tracking_mode !== 'full' ? ` · tracking ${t.tracking_mode}` : ''}</Sub></td>
              <td><Pill code={t.status_code} /></td><td>{t.owner_name || '—'}</td><td className="nowrap">{fmtDate(t.step_end)}</td>
              <td className="nowrap"><button className="quiet" onClick={() => moveTrack(t.tid)}>Move</button></td></tr>
          ))}
        </tbody></table></div>
      ) : <Empty>No tracks yet. A track is one deliverable: a send, a post, a page, a call block.</Empty>}
    </section>
    <LinksAndPages kind="campaign" campaign={c} tracks={d.tracks} />
    <HubSpotSection kind="campaign" entityId={c.id} refId={c.mid} />
    <Scorecard campaign={c} tracks={d.tracks} />
  </>);
}
